import React, { useState } from 'react';
import cx from 'classnames';
import { Popover, Tooltip } from 'antd';
import { useSelector } from 'react-redux';
import Icon from '@ant-design/icons';

import { IUser } from '../../../slices/users/user';
import Avatar from '../avatar';
import { GetHelp } from './GetHelp';
import { SxTooltip } from '../utils/wrapComponents';
import { ReactComponent as AccountIcon } from '../../../assets/sightx-icons/svg/sx-account.svg';
import { ReactComponent as UsageIcon } from '../../../assets/sightx-icons/svg/sx-file.svg';
import { ReactComponent as AdminIcon } from '../../../assets/sightx-icons/svg/sx-lock.svg';
import { ReactComponent as LogoutIcon } from '../../../assets/sightx-icons/svg/sx-logout.svg';
import { ReactComponent as HomeIcon } from '../../../assets/sightx-icons/svg/sx-home.svg';
import { ReactComponent as SurveyIcon } from '../../../assets/sightx-icons/svg/sx-survey.svg';
import { ReactComponent as BrushIcon } from '../../../assets/sightx-icons/svg/sx-brush.svg';
import { ReactComponent as AudiencesIcon } from '../../../assets/sightx-icons/svg/sx-users.svg';
import { ReactComponent as DownIcon } from '../../../assets/sightx-icons/svg/icon-down.svg';

import { UserTypes } from '../domain/userTypes';
import { AccountStates } from '../../../slices/accounts/account';
import Option from './Option';
import { AudienceOption } from './AudienceOption';
import { UpgradeToProCTA } from './UpgradeToProButton';
import { AccessTypes } from '../domain/accountAccessType';
import { selectors, tooltipMessage } from '../../../slices/auth/authSelectors';
import { isNetquestClient } from '../../../utils/clients';
import { BetaTag } from '../betaTag';
import { LibraryOption } from './LibraryOption';
import { Logo } from './Logo';
import { selectors as accountSelectors } from '../../../slices/accounts/accountSelectors';
import { isPermissionHidden } from '../../../utils/accessControl/index.helper';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { FeatureToggle, Off, On } from '../../../featureToggles';

import stylesOld from './leftMenuOld.module.scss';
import stylesForLeftNavResize from './leftMenu.module.scss';

const styles = isFeatureEnabled(Features.LeftNavResize) ? stylesForLeftNavResize : stylesOld;

const projectsLabel = isFeatureEnabled(Features.LeftNavResize) ? 'Projects' : 'My Projects';

interface IProps {
  user: IUser | null;
  onClickLogout: () => void;
  coreUrl: string;
  queryObjects: string;
}

const renderUserLabelTooltip = (text: string, className: string) => {
  return (
    <span ref={React.createRef()} className={cx(styles.singleLine, className)}>
      {text}
    </span>
  );
};

const renderLabelWithIcon = (text: string, icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>, className?: string) => {
  return (
    <span className={cx(styles.singleLineWithIcon, className)}>
      <div className={styles.text}>{text}</div>
      <Icon component={icon} className={styles.icon}/>
    </span>
  );
};

const getUserInfo = (user: IUser | null) => {
  const userEmail = user?.email ?? '';
  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '';

  return (
    <div className={styles.userInfo}>
      <Avatar />
      <FeatureToggle feature={Features.LeftNavResize}>
        <On>
          <div className={styles.accountAndEmail}>
            {renderLabelWithIcon('My account', DownIcon)}
            <SxTooltip
              Component={renderUserLabelTooltip(userEmail, styles.email)}
              tooltipText={userEmail}
              placement="bottom"
              trigger='click'
            />
          </div>
        </On>
        <Off>
          <div className={styles.userAndEmail}>
            <SxTooltip
              Component={renderUserLabelTooltip(userName, styles.username)}
              tooltipText={userName}
              placement="bottom"
            />
            <SxTooltip
              Component={renderUserLabelTooltip(userEmail, styles.email)}
              tooltipText={userEmail}
              placement="bottom"
            />
          </div>
        </Off>
      </FeatureToggle>
    </div>);
};

export const LeftNavBar = (props: IProps): JSX.Element => {
  const sectionSecurityPermissions = useSelector(selectors.getSectionSecurityPermissions);
  const hasBuildAccess = sectionSecurityPermissions?.build ?? false;
  const hasBrandingAccess = sectionSecurityPermissions?.branding ?? false;
  const [isPopUpVisible, setIsPopUpVisible] = useState(false);
  const { coreUrl, queryObjects, user } = props;
  const userInfoContainer = getUserInfo(user);
  const getPopupContainer = (userInfoContainer: HTMLElement) => userInfoContainer.parentElement || document.body;
  const isSightXAdmin = user && user.userType === UserTypes.SxAdmin;
  const isMainAdmin = user && user.userType === UserTypes.MainAdmin;
  const showAdmin = isSightXAdmin || isMainAdmin;
  const accountStatus = user?.account.status;
  const isLapsedAccount = accountStatus === AccountStates.LAPSED;
  const isLiteUser = user && user.account.accessType === AccessTypes.Lite;
  const isEnterpriseAccount = user && user.account.accessType === AccessTypes.Enterprise;
  const recipientsCommunityStatus = useSelector(accountSelectors.getRecipientsCommunityStatus);

  const handleIsPopUpVisible = () => setIsPopUpVisible((prevValue) => !prevValue);
  const handleVisibleChange = (value: boolean) => setIsPopUpVisible(value);

  const getRoutes = () => {
    return <>
      <AudienceOption
        href={`${coreUrl}/audiences${queryObjects}`}
        isLapsedAccount={isLapsedAccount}
      />
      <LibraryOption
        href={`${coreUrl}/questionLibrary${queryObjects}`}
        isLapsedAccount={isLapsedAccount}
      />
      <Option
        key="Usage"
        href={`${coreUrl}/projectOverview${queryObjects}`}
        className={styles.usage}
      >
        { isFeatureEnabled(Features.LeftNavResize) ? <SurveyIcon /> : <UsageIcon />}
        Usage
      </Option>
      { isFeatureEnabled(Features.LeftNavResize) ? null : <hr /> }
      <Option
        key="Account"
        href="/settings/profile"
      >
        <AccountIcon />Account
      </Option>
      { isFeatureEnabled(Features.LeftNavResize) ? <hr /> : null }
      {
        showAdmin &&
        <Option
          key="Admin"
          href="/"
        >
          <AdminIcon />Admin
        </Option>
      }
      {
        <Option
          key="Log out"
          href="/logout"
        >
          <LogoutIcon />Log out
        </Option>
      }
    </>;
  };

  const homePath = `${coreUrl}${queryObjects}`;
  const projectsUrl = `${coreUrl}/projects${queryObjects}`;
  const brandingUrl = `${coreUrl}/branding`;
  const communitiesUrl = `${coreUrl}/communities`;

  // TODO Disabled until clarification is provided regarding the policies and sections related to Access Permissions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderBrandingOption = () => {
    if (!isEnterpriseAccount) {
      return null;
    }
    if (!hasBrandingAccess) {
      return <Tooltip
        placement="right"
        overlayClassName={styles.tooltipOverlay}
        title={tooltipMessage}
      >
        <a className={cx(styles.linkButton, { [styles.disabled]: !hasBrandingAccess })}>
          <div className={styles.label}><BrushIcon className={styles.icon} /><span>Branding <BetaTag className={styles.betaTag}/></span></div>
          <AdminIcon />
        </a>
      </Tooltip>;
    }
    return <a href={brandingUrl} className={cx(styles.linkButton)}>
      <div className={styles.label}><BrushIcon className={styles.icon} /><span>Branding <BetaTag className={styles.betaTag} /></span></div>
    </a>;
  };

  const renderCommunitiesOption = () => {
    if (isPermissionHidden(recipientsCommunityStatus)) {
      return null;
    }

    const option = <div className={styles.label}>
      <AudiencesIcon className={cx(styles.icon, { [styles.communitiesIcon] : isFeatureEnabled(Features.LeftNavResize) })} />
      <span>Communities</span>
    </div>;

    return <a href={communitiesUrl} className={cx(styles.linkButton)}>
      {option}
    </a>;
  };

  // TODO: Rename method to renderMyProjects when removing Features.NewProjectDashboard.
  const renderHomeOption = () => {
    if (isFeatureEnabled(Features.NewProjectDashboard)) {
      return <a href={homePath} className={cx(styles.linkButton)}>
        <div className={styles.label}><HomeIcon className={styles.icon} /><span>{projectsLabel}</span></div>
      </a>;
    }
    if (!hasBuildAccess || isNetquestClient()) {
      return <a href={projectsUrl} className={cx(styles.linkButton)}>
        <div className={styles.label}><SurveyIcon className={styles.icon} /><span>{projectsLabel}</span></div>
      </a>;
    }
    return <a href={homePath} className={cx(styles.linkButton)}>
      <div className={styles.label}><HomeIcon className={styles.icon} /><span>Home</span></div>
    </a>;
  };

  // TODO: Remove this method when removing Features.NewProjectDashboard.
  const renderMyProjects = () => {
    if (isFeatureEnabled(Features.NewProjectDashboard)) {
      return null;
    }
    if (!hasBuildAccess || isNetquestClient()) {
      return null;
    }
    return <a href={projectsUrl} className={cx(styles.linkButton)}>
      <div className={styles.label}><SurveyIcon className={styles.icon} /><span>{projectsLabel}</span></div>
    </a>;
  };

  return (
    <div className={styles.leftNavigation}>
      <Logo />
      {isLiteUser && <UpgradeToProCTA />}
      <div className={styles.initialOptions}>
        {renderHomeOption()}
        {renderMyProjects()}
        {renderCommunitiesOption()}
        {
          // TODO Disabled until clarification is provided regarding the policies and sections related to Access Permissions
          //renderBrandingOption()
        }
      </div>
      <div className={styles.userMenu}>
        <Popover
          overlayClassName={styles.userConfigMenuPopup}
          trigger="click"
          visible={isPopUpVisible}
          getPopupContainer={getPopupContainer}
          onVisibleChange={handleVisibleChange}
          content={
            <div className={styles.userConfigMenu} onClick={handleIsPopUpVisible}>
              {getRoutes()}
            </div>
          }
          placement={isFeatureEnabled(Features.LeftNavResize) ? 'topLeft' : undefined}
        >
          <div onClick={handleIsPopUpVisible}>{userInfoContainer}</div>
        </Popover>
        <GetHelp />
      </div>
    </div>
  );
};
